/** Manages RMA list data fetching. All three endpoints (list, filters, list-by-ids) exist on both
 *  backends and the caller states which one it wants: the redesigned page needs rms-api via
 *  RmsApiConnectionManager, the legacy page is served by the shopify-app proxy. Optional
 *  `include_tab_counts` (rms-api only) requests Open/Closed/All aggregate counts on the list response.
 */
// eslint-disable-next-line
import { injectable } from "inversify";
import { rootContainer, TYPE } from "../container/root";
import { IApiResponse, IHttpService } from "../http-service";
import { IFetchListArgs, IPagedData } from "../../stores/abstract-paged-data-store";
import { createPagedRequestURLSearchParams } from "../helpers";
import { RMA_STATUS } from "../../pages/manage-returns/rma-page/rma-types";
import { IResolutionCategoryPreset } from "../../pages/policies/policy-types";
import RmsApiConnectionManager from "../rms-api-connection-manager";

/**
 * Backend serving a Manage Returns request. Fixed per page: the redesigned list cannot use the proxy
 * (no bucket data, no tab counts, no status groups) and the legacy list has no use for rms-api.
 */
export enum MANAGE_RETURNS_BACKEND {
    LARAVEL_PROXY = 'LARAVEL_PROXY',
    RMS_API = 'RMS_API',
}

/** This is not one to one Rma model's status, it computed on server and it considers shipment status too */
export enum RMA_STATUS_TRANSLATION_KEY {
    PENDING = 'PENDING',
    DONE = 'DONE',
    APPROVED = 'APPROVED',
    RECEIVED = 'RECEIVED',
    REJECTED = 'REJECTED',
    CANCELED = 'CANCELED',
}

export enum RMA_PROVIDERS {
    REFUND_ID = 'refundid',
    REVENI = 'reveni',
    RISE = 'rise',
}

export enum RESTOCK_VALIDATED_TYPE {
    ALL = 'all',
    VALIDATED = 'filter_restock_is_validated',
    NOT_VALIDATED = 'filter_restock_is_not_validated',
    PARTIALLY_VALIDATED = 'filter_restock_is_partially_validated'
}

export enum PAYMENT_STATUS_TYPE {
    ALL = 'all',
    NOT_REFUNDED = 'not_refunded',
    REFUNDED = 'refunded',
    NOT_PAID = 'not_paid',
    PAID = 'paid',
    EXPIRING = 'expiring',
}

export enum RESERVED_ITEM_EXPIRATION_TYPE {
    ALL = 'all',
    DATE_RANGE = 'date_range',
    DAYS_REMAINING = 'days_remaining',
    EXPIRED = 'expired',
}

/**
 * Manual-action bucket types for the "Actions status" filter group. Values mirror the
 * rms-api `manual_actions` contract; the API only returns the ones visible for the store's settings,
 * so the client renders whatever it receives rather than hard-coding a visible subset.
 */
export enum MANUAL_ACTION_TYPE {
    APPROVE = 'approve',
    MANAGER_APPROVAL = 'manager_approval',
    RECEIVE = 'receive',
    REFUND = 'refund',
    RELEASE_EXCHANGE = 'release_exchange',
    VALIDATE = 'validate',
    RESTOCK = 'restock',
    MARK_DONE = 'mark_done',
}


/**
 * On track section types for the "Actions status" filter group. Values mirror the rms-api `on_track`
 * contract; the API only returns non-zero sections, so the client renders whatever it receives.
 */
export enum ON_TRACK_TYPE {
    AWAITING_PAYMENT = 'awaiting_payment',
    RETURN_NOT_SHIPPED = 'return_not_shipped',
    RETURN_IN_TRANSIT = 'return_in_transit',
    AUTOMATION_SCHEDULED = 'automation_scheduled',
}

export enum IRmaListTab {
    STATUS_GROUP_CLOSED = 'CLOSED',
    STATUS_GROUP_OPEN = 'OPEN',
    STATUS_GROUP_ALL = 'ALL',
}
export interface IRmaListItem {
    id: number;
    order_name: string;
    /** Platform customer ID when rms-api adds a dedicated field; until then rms-api maps the id into `_customer_name`. */
    _customer_id?: string;
    _customer_name: string;
    amount: number;
    is_gift: boolean;
    is_out_of_policy: boolean;
    last_unresolved_error: string | null;
    status: RMA_STATUS;
    reserve_item_count: number;
    _release_date: string | null;
    is_returngo_resolution: boolean;
    has_exchange: boolean;
    no_offers: number | null;
    display_date: string;
    _order_url: string,
    _customer_url: string,
    _status: RMA_STATUS_TRANSLATION_KEY,
    _images_urls: string[],
    _reserve_until_date: string | null,
    _item_reservation_timeframe: number,
    _is_instant_exchange: boolean,
    _exchange_draft_order_id: number | null,
    _resolution_name_key: string,
    _resolution_internal_name: string,
    _has_multiple_resolutions: boolean,
    _refund_provider: RMA_PROVIDERS | null,
    _credit_integration_provider: RMA_PROVIDERS | null,
}

export interface IFetchRmaListArgs extends IFetchListArgs {
    search_input: string;
    search_exact_match: boolean;
    filter_resolution_id: number | null;
    filter_return_reason_id: number | null;
    filter_restock_validated_type: RESTOCK_VALIDATED_TYPE;
    filter_date_range: string | null;
    filter_status: string | null;
    filter_reserve_item_expiration: RESERVED_ITEM_EXPIRATION_TYPE;
    filter_reserve_item_expiration_input: string;
    filter_payment_status_type: PAYMENT_STATUS_TYPE;
    /**
     * Selected manual-action buckets. Sent OR-combined as a comma-separated `manual_actions`
     * query param; omitted when empty. Only used by the redesigned page's Open tab.
     */
    filter_manual_actions?: string[];
    /**
     * Selected error-bucket labels. Sent OR-combined as REPEATED `error_labels` query params
     * (not comma-joined — labels are free text and may contain commas); omitted when empty. Redesigned
     * page's Open tab only.
     */
    filter_error_labels?: string[];
    /**
     * Selected On track sections, OR-combined as a comma-separated `on_track` param; omitted when empty.
     * Comma-joined rather than repeated like `error_labels` — a closed enum, never free text.
     */
    filter_on_track?: string[];
    /**
     * Optional: omitted on the legacy page (which has no tab UI). When sent, it must be paired
     * with a compatible `filter_status` -- the rms-api enforces this as a 400 cross-field check.
     */
    status_group?: IRmaListTab;
    /**
     * Opt-in flag for the `tab_counts` block on the paginated response. Sent by the redesigned
     * page's paged store on every request (the page always renders Open/Closed/All counts);
     * the legacy page never sends it.
     */
    include_tab_counts?: boolean;
}

/**
 * The filters shared by GET /manage-returns and GET /manage-returns/filters — the ones that scope the
 * `manual_actions` / `errors` bucket counts.
 *
 * `filter_status` is included: a bucket spans many RMA statuses, so the chip is a real narrowing. The
 * tab and the two bucket selections are deliberately absent, so they cannot reach the endpoint and
 * narrow a count that describes them.
 */
export type IFetchRmaFiltersArgs = Partial<
    Pick<
        IFetchRmaListArgs,
        | "search_input"
        | "search_exact_match"
        | "filter_resolution_id"
        | "filter_return_reason_id"
        | "filter_restock_validated_type"
        | "filter_date_range"
        | "filter_status"
        | "filter_reserve_item_expiration"
        | "filter_reserve_item_expiration_input"
        | "filter_payment_status_type"
    >
>;

export interface IRmaTabCounts {
    open: number;
    closed: number;
    all: number;
}

/**
 * Paginated GET /manage-returns (and Laravel proxy) response envelope.
 * `tab_counts` is optional metadata on the list response (sibling of `data`, `total`, etc.),
 * present only when the request sends `include_tab_counts=true`. Not a field on `IRmaListItem`.
 */
export interface IRmaListPagedData extends IPagedData<IRmaListItem> {
    tab_counts?: IRmaTabCounts;
}

export interface IReturnReason {
    id: number,
    name: string,
}

export interface IManualActionOption {
    value: string,
    count: number,
}

export interface IManualActions {
    total: number,
    options: IManualActionOption[],
}

/** One error-bucket sub-row: a merchant-facing error label and the count of open RMAs carrying it. */
export interface IErrorBucketOption {
    value: string,
    count: number,
}

/**
 * The `errors` bucket: open RMAs with unresolved errors aggregated by error label, plus the
 * distinct grand total. Independent of manual actions / automation tracking.
 */
export interface IErrors {
    total: number,
    options: IErrorBucketOption[],
}

/** One On track sub-row: a section and the count of open RMAs currently in it. */
export interface IOnTrackOption {
    value: string,
    count: number,
}

/**
 * The `on_track` bucket: open RMAs progressing with nothing for the merchant to do.
 *
 * `total` is NOT the union of `options` — the parent is the residual "open minus Error minus Manual", so
 * it may be non-zero with `options` empty. Render the card row from `total` alone.
 */
export interface IOnTrack {
    total: number,
    options: IOnTrackOption[],
}

export interface IRmaListFilterOptions {
    resolutions: IResolutionCategoryPreset[],
    return_reasons: IReturnReason[],
    /** Present only when the rms-api automation-tracking read side is active; absent on older backends. */
    manual_actions?: IManualActions,
    /** Error buckets. Present on rms-api; absent on the legacy Laravel proxy. */
    errors?: IErrors,
    /** On track buckets. Same availability gate as `manual_actions` — both read `rma_actions`. */
    on_track?: IOnTrack,
}

/**
 * Appends the dimension filters shared by the list and filters endpoints.
 *
 * Truthy-only, matching long-standing list behaviour. Single source of truth for both callers, so a
 * param cannot be formatted one way on the list and another on the counts it is compared against.
 */
function appendDimensionFilterParams(searchParams: URLSearchParams, args: IFetchRmaFiltersArgs): void {
    if (args.search_input) {
        searchParams.append('search_input', args.search_input);
    }
    if (args.search_exact_match) {
        searchParams.append('search_exact_match', '1');
    }
    if (args.filter_resolution_id) {
        searchParams.append('resolution_id', args.filter_resolution_id.toString());
    }
    if (args.filter_return_reason_id) {
        searchParams.append('return_reason_id', args.filter_return_reason_id.toString());
    }
    if (args.filter_restock_validated_type) {
        searchParams.append('restock_validated_type', args.filter_restock_validated_type);
    }
    if (args.filter_date_range) {
        searchParams.append('date_range', args.filter_date_range);
    }
    if (args.filter_status) {
        searchParams.append('status', args.filter_status);
    }
    if (args.filter_reserve_item_expiration) {
        searchParams.append('reserve_item_expiration', args.filter_reserve_item_expiration);
    }
    if (args.filter_reserve_item_expiration_input) {
        searchParams.append('reserve_item_expiration_input', args.filter_reserve_item_expiration_input);
    }
    if (args.filter_payment_status_type) {
        searchParams.append('payment_status_type', args.filter_payment_status_type);
    }
}

@injectable()
class RmaListService {
    private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);
    private readonly rmsApiConnection: RmsApiConnectionManager = rootContainer.get<RmsApiConnectionManager>(TYPE.RmsApiConnectionManager);

    /**
     * Fetches a paginated RMA list from the requested backend:
     *   - `RMS_API`       -> rms-api `GET /manage-returns`
     *   - `LARAVEL_PROXY` -> `GET /rms-api/manage-returns/rma-list` (shopify-app proxy)
     *
     * Two params are redesigned-UI only, so the legacy paged store omits them:
     *   - `status_group` -- Open/Closed/All tab scope (its default would 400 against terminal
     *     `filter_status` chips on rms-api)
     *   - `include_tab_counts` -- aggregate counts for tab labels; `tab_counts` comes back only
     *     when it was sent
     */
    public async fetchRmaList(
        backend: MANAGE_RETURNS_BACKEND,
        args: IFetchRmaListArgs,
    ): Promise<IRmaListPagedData> {
        /** Only the params `appendDimensionFilterParams` does not own. */
        const {
            page,
            perPage,
            sortBy,
            sortDir,
            filter_manual_actions,
            filter_error_labels,
            filter_on_track,
            status_group,
            include_tab_counts,
        } = args;
        const searchParams = createPagedRequestURLSearchParams(page, perPage, sortBy, sortDir);
        appendDimensionFilterParams(searchParams, args);
        if (filter_manual_actions?.length) {
            searchParams.append('manual_actions', filter_manual_actions.join(','));
        }
        if (filter_error_labels?.length) {
            // Repeated param (not comma-joined): error labels are free text and may contain commas.
            filter_error_labels.forEach((label) => searchParams.append('error_labels', label));
        }
        if (filter_on_track?.length) {
            searchParams.append('on_track', filter_on_track.join(','));
        }
        if (status_group) {
            searchParams.append("status_group", status_group);
        }
        if (include_tab_counts === true) {
            searchParams.append("include_tab_counts", "true");
        }

        if (backend === MANAGE_RETURNS_BACKEND.RMS_API) {
            const res = await this.rmsApiConnection.get<IApiResponse<IRmaListPagedData>>(
                `/manage-returns?${searchParams.toString()}`
            );
            return res.payload;
        }

        const res = await this.httpService.get<IApiResponse<IRmaListPagedData>>(
            `/rms-api/manage-returns/rma-list?${searchParams.toString()}`
        );
        return res.payload;
    }

    /**
     * Filter options plus the Action-required bucket counts.
     *
     * `args` scopes the counts only — `resolutions` / `return_reasons` are filter-independent. Every
     * param is optional server-side, so the no-args call the legacy page makes is unchanged. The
     * Laravel proxy has no bucket data, so the params are omitted there rather than sent unused.
     */
    public async fetchFilters(
        backend: MANAGE_RETURNS_BACKEND,
        args: IFetchRmaFiltersArgs = {},
    ): Promise<IRmaListFilterOptions> {
        if (backend === MANAGE_RETURNS_BACKEND.RMS_API) {
            const searchParams = new URLSearchParams();
            appendDimensionFilterParams(searchParams, args);
            const query = searchParams.toString();
            const res = await this.rmsApiConnection.get<IApiResponse<IRmaListFilterOptions>>(
                `/manage-returns/filters${query ? `?${query}` : ''}`
            );
            return res.payload;
        }

        const res = await this.httpService.get<IApiResponse<IRmaListFilterOptions>>(`/rms-api/manage-returns/filters`);
        return res.payload;
    }

    public async fetchRmaListByIds(backend: MANAGE_RETURNS_BACKEND, csvIdList: string): Promise<IRmaListItem[]> {
        if (backend === MANAGE_RETURNS_BACKEND.RMS_API) {
            const res = await this.rmsApiConnection.get<IApiResponse<IRmaListItem[]>>(
                `/manage-returns/rma-list-by-ids?ids=${csvIdList}`
            );
            return res.payload;
        }

        const res = await this.httpService.get<IApiResponse<IRmaListItem[]>>(`/rms-api/manage-returns/rma-list-by-ids?ids=${csvIdList}`);
        return res.payload;
    }

}


export default RmaListService;
